import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, MapPin, Upload, X } from 'lucide-react';
import LocationMap from './LocationMap';
import ImageCropper from './ImageCropper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local date input (dd/mm/yyyy) — mirrors DateInput in AnimalForm.
function DateInput({ value, onChange, className = '', required = false }) {
  const formatDisplay = (v) => { if (!v) return ''; const p = v.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ''; };
  const [display, setDisplay] = useState(formatDisplay(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDisplay(formatDisplay(value)); }, [value, focused]);
  const onDisplayChange = (text) => {
    let digits = text.replace(/\D/g, '').slice(0, 8);
    let out = '';
    if (digits.length >= 1) out += digits.slice(0, 2);
    if (digits.length >= 3) out += '/' + digits.slice(2, 4);
    if (digits.length >= 5) out += '/' + digits.slice(4, 8);
    setDisplay(out);
    if (digits.length === 8) {
      const [dd, mm, yyyy] = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
      onChange(`${yyyy}-${mm}-${dd}`);
    } else if (digits.length === 0) {
      onChange('');
    }
  };
  const isoForPicker = value && value.length >= 10 ? value.slice(0, 10) : '';
  return (
    <div className={`relative inline-flex items-center w-full ${className}`}>
      <input value={display} onChange={e => onDisplayChange(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        placeholder="dd/mm/yyyy" required={required}
        className="flex-1 rounded-xl border border-border bg-background px-3 py-2 pr-9 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
      <input type="date" value={isoForPicker}
        onChange={e => { if (e.target.value) { onChange(e.target.value); setDisplay(formatDisplay(e.target.value)); } }}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0 cursor-pointer"
        aria-label="Selecionar data no calendário" />
      <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  );
}

const MOTIVOS_SEQUESTRO = [
  'Agressão a pessoas',
  'Agressão a outro animal',
  'Cão perigoso',
  'Por ordem judicial',
  'Outro'
];

export default function SequestroForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [cropperFiles, setCropperFiles] = useState(null);
  const [chipError, setChipError] = useState('');
  const [form, setForm] = useState({
    nome: '',
    especie: 'canina',
    sexo: '',
    idade: '',
    data_inicio_sequestro: new Date().toISOString().slice(0, 10),
    chip: '',
    data_vacina_raiva: '',
    num_boletim: '',
    raca: '',
    cor: '',
    motivo_sequestro: '',
    local_ocorrencia: '',
    num_porta: '',
    codigo_postal: '',
    freguesia: '',
    concelho: '',
    titular: true,
    dados_titular: '',
    esterilizado: false,
    observacoes: '',
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const setChip = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 15);
    set('chip', digits);
    if (digits.length > 0 && digits.length !== 15) setChipError(`${digits.length}/15 dígitos`);
    else setChipError('');
  };

  // Geocoding preview
  const [geoPreview, setGeoPreview] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const fetchGeocode = useCallback(async (local, concelho, num_porta, codigo_postal, freguesia) => {
    if (!local || local.trim().length < 3) { setGeoPreview(null); return; }
    setGeoLoading(true);
    try {
      const r = await axios.get(`${API}/animais/geocoding/search`, { params: { q: local, concelho, num_porta, codigo_postal, freguesia } });
      if (r.data && r.data.lat) {
        setGeoPreview(r.data);
        setForm(f => ({ ...f,
          local_ocorrencia_lat: r.data.lat,
          local_ocorrencia_lng: r.data.lng,
          local_ocorrencia_display: r.data.display_name,
        }));
      } else setGeoPreview(null);
    } catch (e) { setGeoPreview(null); }
    finally { setGeoLoading(false); }
  }, []);
  useEffect(() => {
    const h = setTimeout(() => fetchGeocode(form.local_ocorrencia, form.concelho, form.num_porta, form.codigo_postal, form.freguesia), 600);
    return () => clearTimeout(h);
  }, [form.local_ocorrencia, form.concelho, form.num_porta, form.codigo_postal, form.freguesia, fetchGeocode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.chip && form.chip.length !== 15) { setChipError('O chip deve ter exactamente 15 dígitos'); return; }
    setChipError('');
    setLoading(true);
    try {
      const payload = {
        ...form,
        tipo_registo: 'sequestro_domiciliario',
        motivo_entrada: 'Sequestro Domiciliário',
        // For sequestros the 'entrada' date is the start of the sequestro
        entrada: form.data_inicio_sequestro,
      };
      const r = await axios.post(`${API}/animais`, payload);
      const animalId = r.data.id;
      if (fotos.length > 0 && animalId) {
        const errors = [];
        let okCount = 0;
        for (const original of fotos) {
          try {
            const { compressImageFile } = await import('../utils/imageCompress');
            const compressed = await compressImageFile(original);
            const fd = new FormData();
            fd.append('file', compressed);
            await axios.post(`${API}/animais/${animalId}/fotos`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 180000,
            });
            okCount++;
          } catch (e) {
            errors.push({ filename: original.name, error: e.response?.data?.detail || e.message || 'Erro' });
          }
        }
        if (errors.length > 0) alert(`${okCount} de ${fotos.length} foto(s) carregadas.\nFalharam:\n${errors.map(x => `• ${x.filename}: ${x.error}`).join('\n')}`);
      }
      navigate('/');
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || err.message || 'Erro desconhecido';
      alert(`Erro ao guardar: ${detail}`);
    }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="sequestro-form-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/')} className="p-2 rounded-full hover:bg-muted transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <h2 className="font-heading font-bold text-2xl">Registar Sequestro Domiciliário</h2>
      </div>
      <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border p-6 space-y-6">
        <h3 className="font-heading font-bold text-lg">Dados do Animal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <SF label="Nome / Nº de Processo *" value={form.nome} onChange={v => set('nome', v)} required testid="sf-nome" />
          <SS label="Espécie *" value={form.especie} onChange={v => set('especie', v)} options={[['canina','Canina'],['felina','Felina']]} />
          <SS label="Sexo *" value={form.sexo} onChange={v => set('sexo', v)} options={[['macho','Macho'],['fêmea','Fêmea']]} required />
          <SF label="Idade" value={form.idade} onChange={v => set('idade', v)} testid="sf-idade" placeholder="ex: 3 anos / 6 meses" />
          <div>
            <label className="text-sm font-medium block mb-1">Data Início de Sequestro *</label>
            <DateInput value={form.data_inicio_sequestro} onChange={v => set('data_inicio_sequestro', v)} className="w-full" required />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Chip (15 dígitos)</label>
            <input value={form.chip} onChange={e => setChip(e.target.value)} maxLength={15} inputMode="numeric" data-testid="sf-chip"
              className={`w-full rounded-xl border bg-background px-3 py-2 text-sm focus:ring-2 focus:outline-none ${chipError ? 'border-red-400 focus:ring-red-300' : 'border-border focus:ring-primary'}`} />
            {chipError && <p className="text-xs text-red-500 mt-0.5">{chipError}</p>}
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Data Vacina Raiva</label>
            <DateInput value={form.data_vacina_raiva} onChange={v => set('data_vacina_raiva', v)} className="w-full" />
          </div>
          <SF label="Nº Boletim" value={form.num_boletim} onChange={v => set('num_boletim', v)} testid="sf-boletim" />
          <SF label="Raça" value={form.raca} onChange={v => set('raca', v)} />
          <SF label="Cor" value={form.cor} onChange={v => set('cor', v)} />
          <SS label="Motivo de Sequestro *" value={form.motivo_sequestro} onChange={v => set('motivo_sequestro', v)} options={MOTIVOS_SEQUESTRO.map(m => [m, m])} required />
          <SF label="Local de Ocorrência" value={form.local_ocorrencia} onChange={v => set('local_ocorrencia', v)} testid="sf-local" />
          <SF label="Nº de Porta" value={form.num_porta} onChange={v => set('num_porta', v)} testid="sf-num-porta" />
          <SF label="Código Postal" value={form.codigo_postal} onChange={v => set('codigo_postal', v)} testid="sf-codigo-postal" placeholder="ex: 2600-131" />
          <SF label="Freguesia" value={form.freguesia} onChange={v => set('freguesia', v)} testid="sf-freguesia" />
          <SF label="Concelho" value={form.concelho} onChange={v => set('concelho', v)} testid="sf-concelho" />
          <SF label="Titular *" value={form.dados_titular} onChange={v => set('dados_titular', v)} required placeholder="Nome e contacto" testid="sf-titular" />
          <label className="flex items-center gap-2 text-sm self-end pb-1">
            <input type="checkbox" checked={form.esterilizado} onChange={e => set('esterilizado', e.target.checked)} data-testid="sf-esterilizado" className="rounded" />
            Esterilizado *
          </label>
        </div>

        {/* Geocoding preview */}
        {(form.local_ocorrencia && form.local_ocorrencia.length >= 3) && (
          <div data-testid="sf-geo-preview" className="bg-muted/30 rounded-xl border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Pré-visualização da Localização</span>
              {geoLoading && <span className="text-xs text-muted-foreground">a procurar...</span>}
              {!geoLoading && !geoPreview && <span className="text-xs text-amber-600">Sem resultados — verifique o endereço e concelho</span>}
            </div>
            {geoPreview && (
              <>
                <LocationMap lat={geoPreview.lat} lng={geoPreview.lng} label={geoPreview.display_name} height={220} />
                <p className="text-xs text-muted-foreground mt-1 italic">{geoPreview.display_name}</p>
              </>
            )}
          </div>
        )}

        <div>
          <label className="text-sm font-medium block mb-1">Observações</label>
          <textarea rows={3} value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
        </div>

        <div>
          <h3 className="font-heading font-bold text-lg mb-2">Fotos do Animal</h3>
          <div className="flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-2 bg-muted hover:bg-border transition-all rounded-xl border border-border px-4 py-3 cursor-pointer text-sm">
              <Upload className="w-4 h-4" /> Adicionar Fotos
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={e => { const files = Array.from(e.target.files || []); if (files.length > 0) setCropperFiles(files); e.target.value = ''; }} />
            </label>
            {fotos.length > 0 && <span className="text-xs text-muted-foreground">{fotos.length} foto{fotos.length > 1 ? 's' : ''} pronta{fotos.length > 1 ? 's' : ''}</span>}
          </div>
          {fotos.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3">
              {fotos.map((f, i) => (
                <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-border bg-background">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setFotos(p => p.filter((_, idx) => idx !== i))}
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="submit" disabled={loading} data-testid="sf-submit"
          className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium hover:brightness-110 transition-all flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" />{loading ? 'A guardar...' : 'Registar Sequestro'}
        </button>
      </form>
      {cropperFiles && (
        <ImageCropper files={cropperFiles}
          onCancel={() => setCropperFiles(null)}
          onDone={(adjusted) => { setFotos(prev => [...prev, ...adjusted]); setCropperFiles(null); }} />
      )}
    </div>
  );
}

function SF({ label, value, onChange, required = false, testid, placeholder }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1">{label}</label>
      <input data-testid={testid} value={value || ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} required={required}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
    </div>
  );
}

function SS({ label, value, onChange, options, required = false }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} required={required}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none">
        <option value="">— Selecionar —</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
